import { ReactNode } from "react";
import clsx from "clsx";
import { ClockIcon, ComputerDesktopIcon } from "@heroicons/react/20/solid";

import { Alert } from "../base/Alert";
import { IImportSyncRun, IImportSyncRunDetail, IImportSyncStatus } from "../../models/ImportSync/ImportSync";
import {
  displayState,
  formatAgo,
  formatDateTime,
  formatDuration,
  formatSourceTime,
  numberFormat,
  runDurationMs,
  SyncStatePill,
  useNow,
} from "./syncMeta";

/** A nightly sync that has not completed for this long means the scheduled task needs a look. */
const OVERDUE_MS = 36 * 60 * 60 * 1000;

export function SyncOverview({ status }: { status: IImportSyncStatus }) {
  const now = useNow(status.current ? 1000 : 30_000);

  return status.current ? (
    <LiveRun run={status.current} now={now} />
  ) : (
    <IdleSummary status={status} now={now} />
  );
}

// -----------------------------------------------------------------------------
// While a sync is running
// -----------------------------------------------------------------------------

function LiveRun({ run, now }: { run: IImportSyncRunDetail; now: number }) {
  const state = displayState(run);
  const preparing = run.totalVehicles === 0 && run.processedVehicles === 0;
  const fraction = run.totalVehicles > 0 ? Math.min(1, run.processedVehicles / run.totalVehicles) : 0;

  const elapsedMs = runDurationMs(run, now);
  const perMinute = elapsedMs > 60_000 ? run.processedVehicles / (elapsedMs / 60_000) : 0;
  const remaining = Math.max(0, run.totalVehicles - run.processedVehicles);
  const etaMs = perMinute > 0 ? (remaining / perMinute) * 60_000 : NaN;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-5">
      <RunHeading run={run} now={now} />

      {state === "Stalled" && (
        <Alert
          severity="warn"
          title={`No report from ${run.machineName ?? "the console"} for ${formatAgo(run.lastHeartbeatOn, now).replace(" ago", "")}`}
          message="The machine may be asleep or offline, or the console was closed. The next scheduled run carries on from the last vehicle it finished."
        />
      )}

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-4">
          <p className="text-3xl font-semibold tabular-nums text-gray-900">
            {preparing ? "Preparing" : `${Math.floor(fraction * 100)}%`}
          </p>
          <p className="text-sm text-gray-500 tabular-nums text-right">
            {preparing
              ? "Finding vehicles that changed in Pinnacle…"
              : `${numberFormat.format(run.processedVehicles)} of ${numberFormat.format(run.totalVehicles)} vehicles`}
          </p>
        </div>

        <div
          className="h-3 w-full overflow-hidden rounded-full bg-gray-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={run.totalVehicles}
          aria-valuenow={run.processedVehicles}
          aria-label="Sync progress"
        >
          {preparing ? (
            <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-300" />
          ) : (
            <div
              className={clsx(
                "h-full rounded-full transition-[width] duration-700 ease-out",
                state === "Stalled" ? "bg-orange-400" : "bg-blue-600",
              )}
              style={{ width: `${fraction * 100}%` }}
            />
          )}
        </div>

        {!preparing && (
          <p className="text-sm text-gray-600 tabular-nums">
            {perMinute > 0 ? `${numberFormat.format(Math.round(perMinute))} vehicles/min` : "Measuring speed…"}
            {isFinite(etaMs) && remaining > 0 && <> · about {formatDuration(etaMs)} left</>}
            <> · running {formatDuration(elapsedMs)}</>
          </p>
        )}
      </div>

      <StatTiles run={run} />
    </section>
  );
}

// -----------------------------------------------------------------------------
// Between syncs
// -----------------------------------------------------------------------------

function IdleSummary({ status, now }: { status: IImportSyncStatus; now: number }) {
  const latest = status.recent[0];
  const lastCompleted = status.lastCompleted;

  if (!latest) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900">No syncs yet</h2>
        <p className="mt-1 text-sm text-gray-600">
          Nothing has been reported by this customer's import console. Once the scheduled task is installed on
          their machine (<code className="rounded bg-gray-100 px-1">Install-DailySync.ps1</code>), the first run
          imports everything and appears here as it goes.
        </p>
      </section>
    );
  }

  const latestState = displayState(latest);
  const interrupted = latestState === "Failed" || latestState === "Abandoned" || latestState === "Cancelled";
  const overdue = lastCompleted?.completedOn && now - new Date(lastCompleted.completedOn).getTime() > OVERDUE_MS;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-500">Last completed sync</p>
          <p className="mt-1 text-3xl font-semibold text-gray-900">
            {lastCompleted ? formatAgo(lastCompleted.completedOn, now) : "Never"}
          </p>
          {lastCompleted && (
            <p className="mt-1 text-sm text-gray-600">
              {formatDateTime(lastCompleted.completedOn)} · took {formatDuration(runDurationMs(lastCompleted, now))} ·
              covers Pinnacle changes up to {formatSourceTime(lastCompleted.snapshotAt)}
            </p>
          )}
        </div>
        {lastCompleted && <SyncStatePill state={displayState(lastCompleted)} />}
      </div>

      {interrupted && (
        <Alert
          severity={latestState === "Cancelled" ? "info" : "error"}
          title={`Latest run ${latestState.toLowerCase()} ${formatAgo(latest.completedOn ?? latest.startedOn, now)}`}
          message={`${latest.error ?? "No reason was recorded."} It got through ${numberFormat.format(latest.processedVehicles)} of ${numberFormat.format(latest.totalVehicles)} vehicles; the next run carries on from there.`}
        />
      )}

      {!lastCompleted && !interrupted && (
        <Alert severity="info" message="The first full sync has not finished yet." />
      )}

      {overdue && (
        <Alert
          severity="warn"
          title="No completed sync in the last 36 hours"
          message={`Check the "Wilma Pinnacle Sync" scheduled task on ${lastCompleted?.machineName ?? "the yard's machine"} — the machine may be off, or the task disabled.`}
        />
      )}

      {lastCompleted && <StatTiles run={lastCompleted} />}
    </section>
  );
}

// -----------------------------------------------------------------------------

function RunHeading({ run, now }: { run: IImportSyncRun; now: number }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <SyncStatePill state={displayState(run)} />
        <span className="text-sm font-medium text-gray-900">
          {run.mode === "Full"
            ? "Full sync"
            : `Changes since ${formatSourceTime(run.changesSince)}`}
        </span>
        {run.resumedFromRunId && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Resumed</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <ComputerDesktopIcon className="h-4 w-4" aria-hidden />
          {run.machineName ?? "Unknown machine"}
        </span>
        <span className="inline-flex items-center gap-1">
          <ClockIcon className="h-4 w-4" aria-hidden />
          Started {formatDateTime(run.startedOn)} · last report {formatAgo(run.lastHeartbeatOn, now)}
        </span>
      </div>
    </div>
  );
}

function StatTiles({ run }: { run: IImportSyncRun }) {
  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile label="Vehicles imported" value={numberFormat.format(run.importedVehicles)} />
      <StatTile
        label="Vehicles failed"
        value={numberFormat.format(run.failedVehicles)}
        accent={run.failedVehicles > 0 ? "border-l-amber-500" : undefined}
        hint={run.failedVehicles > 0 ? "Retried next run" : undefined}
      />
      <StatTile label="Images uploaded" value={numberFormat.format(run.imagesUploaded)} />
      <StatTile
        label="Images already there / failed"
        value={
          <>
            {numberFormat.format(run.imagesSkipped)}
            <span className="text-gray-400"> / </span>
            {numberFormat.format(run.imagesFailed)}
          </>
        }
        accent={run.imagesFailed > 0 ? "border-l-amber-500" : undefined}
      />
    </dl>
  );
}

function StatTile({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: ReactNode;
  accent?: string;
  hint?: string;
}) {
  return (
    <div className={clsx("rounded-md border border-gray-200 border-l-4 bg-gray-50 px-3 py-2", accent ?? "border-l-gray-300")}>
      <dt className="text-xs font-medium text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{value}</dd>
      {hint && <dd className="text-xs text-gray-500">{hint}</dd>}
    </div>
  );
}
